// src/pages/connections/ConnectionForm.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaPlug,
  FaShieldAlt,
  FaUser,
  FaGasPump,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaTools,
  FaInfoCircle,
  FaBuilding,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getConnectionById,
  createConnection,
  updateConnection,
} from "../../api/tenant/connection.api";

// ✅ Import customer API
import { getCustomers } from "../../components/services/customerService";

// ✅ Import master data APIs
import { brandApi, cylinderTypeApi } from "../../api/superadmin/masterData.api";

const ConnectionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [cylinderTypes, setCylinderTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  const [formData, setFormData] = useState({
    customerId: "",
    connectionNumber: "",
    gasType: "lpg",
    cylinderCapacity: "14.2kg",
    cylinderTypeId: "",
    brandId: "",
    connectionDate: new Date().toISOString().split("T")[0],
    securityDeposit: 0,
    regulatorNumber: "",
    regulatorType: "standard",
    stoveDetails: "",
    connectionStatus: "active",
  });
  const [validated, setValidated] = useState(false);

  // Load dropdown data
  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    setLoadingDropdowns(true);
    try {
      // ✅ Load customers
      try {
        const customersList = await getCustomers({ limit: 1000 });
        setCustomers(Array.isArray(customersList) ? customersList : []);
        console.log("✅ Customers loaded:", customersList?.length || 0);
      } catch (error) {
        console.error("Failed to load customers:", error);
        if (error.response?.status !== 404) {
          toast.error("Failed to load customers");
        }
        setCustomers([]);
      }

      // Load cylinder types
      try {
        const cylinderTypesResponse = await cylinderTypeApi.getAll();
        let cylinderTypesList = [];
        if (Array.isArray(cylinderTypesResponse)) {
          cylinderTypesList = cylinderTypesResponse;
        } else if (cylinderTypesResponse?.data && Array.isArray(cylinderTypesResponse.data)) {
          cylinderTypesList = cylinderTypesResponse.data;
        } else if (cylinderTypesResponse?.data?.data && Array.isArray(cylinderTypesResponse.data.data)) {
          cylinderTypesList = cylinderTypesResponse.data.data;
        }
        setCylinderTypes(cylinderTypesList);
        console.log("✅ Cylinder types loaded:", cylinderTypesList.length);
      } catch (error) {
        console.error("Failed to load cylinder types:", error);
        if (error.response?.status !== 404) {
          toast.error("Failed to load cylinder types");
        }
        setCylinderTypes([]);
      }

      // Load brands
      try {
        const brandsResponse = await brandApi.getAll();
        let brandsList = [];
        if (Array.isArray(brandsResponse)) {
          brandsList = brandsResponse;
        } else if (brandsResponse?.data && Array.isArray(brandsResponse.data)) {
          brandsList = brandsResponse.data;
        } else if (brandsResponse?.data?.data && Array.isArray(brandsResponse.data.data)) {
          brandsList = brandsResponse.data.data;
        }
        setBrands(brandsList);
        console.log("✅ Brands loaded:", brandsList.length);
      } catch (error) {
        console.error("Failed to load brands:", error);
        if (error.response?.status !== 404) {
          toast.error("Failed to load brands");
        }
        setBrands([]);
      }

    } catch (error) {
      console.error("Failed to load dropdown data:", error);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  useEffect(() => {
    if (isEditing) {
      loadConnection();
    }
  }, [id]);

  const loadConnection = async () => {
    setLoading(true);
    setError("");
    try {
      const connection = await getConnectionById(id);
      setFormData({
        customerId: connection.customer_id || "",
        connectionNumber: connection.connection_number || "",
        gasType: connection.gas_type || "lpg",
        cylinderCapacity: connection.cylinder_capacity || "14.2kg",
        cylinderTypeId: connection.cylinder_type_id || "",
        brandId: connection.brand_id || "",
        connectionDate: connection.connection_date
          ? new Date(connection.connection_date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        securityDeposit: connection.security_deposit || 0,
        regulatorNumber: connection.regulator_number || "",
        regulatorType: connection.regulator_type || "standard",
        stoveDetails: connection.stove_details || "",
        connectionStatus: connection.connection_status || "active",
      });
    } catch (error) {
      console.error("Failed to load connection:", error);
      setError(error.response?.data?.message || "Failed to load connection");
      toast.error("Failed to load connection");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (e.currentTarget.checkValidity() === false) {
      return;
    }

    // ✅ Validate that a customer is selected
    if (!formData.customerId) {
      toast.error("Please select a customer");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      // ✅ Prepare data for API - ensure numeric values
      const payload = {
        ...formData,
        customerId: parseInt(formData.customerId), // ✅ Convert to number
        securityDeposit: parseFloat(formData.securityDeposit) || 0,
        cylinderTypeId: formData.cylinderTypeId ? parseInt(formData.cylinderTypeId) : null,
        brandId: formData.brandId ? parseInt(formData.brandId) : null,
      };

      console.log("📦 Submitting payload:", payload);

      if (isEditing) {
        await updateConnection(id, payload);
        toast.success("Connection updated successfully!");
      } else {
        await createConnection(payload);
        toast.success("Connection created successfully!");
      }
      navigate("/connections");
    } catch (error) {
      console.error("Failed to save connection:", error);
      
      // ✅ Better error message
      let errorMsg = "Failed to save connection";
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
        <div className="text-center py-5">
          <Spinner animation="border" variant="secondary" size="lg" />
          <h4 className="mt-3">Loading connection...</h4>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 py-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />

      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/connections")}
            className="mb-2"
            style={{ 
              borderRadius: "8px",
              borderColor: "#dee2e6",
              color: "#495057",
              fontSize: "14px",
              padding: "8px 16px",
            }}
          >
            <FaArrowLeft className="me-2" /> Back
          </Button>
          <h3 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>
            {isEditing ? (
              <>
                <FaPlug className="me-2" style={{ color: "rgb(30, 58, 111)" }} />
                Edit Connection
                <Badge bg="secondary" className="ms-2" style={{ fontSize: "11px" }}>
                  #{id}
                </Badge>
              </>
            ) : (
              <>
                <FaPlug className="me-2" style={{ color: "rgb(30, 58, 111)" }} />
                New Connection
              </>
            )}
          </h3>
          <p className="text-muted mt-1" style={{ fontSize: "14px" }}>
            {isEditing 
              ? "Update the connection details below" 
              : "Fill in the details to create a new connection"}
          </p>
        </div>
        {isEditing && (
          <div className="mt-2 mt-md-0">
            <Badge 
              bg={formData.connectionStatus === "active" ? "success" : 
                  formData.connectionStatus === "suspended" ? "warning" :
                  formData.connectionStatus === "terminated" ? "danger" : "secondary"}
              style={{ fontSize: "12px", padding: "6px 14px" }}
            >
              Status: {formData.connectionStatus?.toUpperCase() || "ACTIVE"}
            </Badge>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError("")} dismissible>
          <FaInfoCircle className="me-2" />
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm rounded-3">
        <Card.Body className="p-4">
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            {/* Basic Information Section */}
            <div className="mb-4">
              <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)", fontSize: "15px" }}>
                <FaPlug className="me-2" /> Basic Information
              </h6>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaUser className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Customer <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="customerId"
                      value={formData.customerId}
                      onChange={handleChange}
                      required
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                      disabled={loadingDropdowns}
                    >
                      <option value="">Select Customer</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name} - {customer.mobile}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select a customer.
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                      {loadingDropdowns ? "Loading customers..." : `${customers.length} customers available`}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaPlug className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Connection Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="connectionNumber"
                      value={formData.connectionNumber}
                      onChange={handleChange}
                      placeholder="Auto-generated"
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                      Leave empty to auto-generate
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaGasPump className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Gas Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="gasType"
                      value={formData.gasType}
                      onChange={handleChange}
                      required
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                    >
                      <option value="lpg">LPG</option>
                      <option value="png">PNG</option>
                      <option value="cng">CNG</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaGasPump className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Cylinder Type
                    </Form.Label>
                    <Form.Select
                      name="cylinderTypeId"
                      value={formData.cylinderTypeId}
                      onChange={handleChange}
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                      disabled={loadingDropdowns}
                    >
                      <option value="">Select Cylinder Type</option>
                      {cylinderTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                      {loadingDropdowns ? "Loading..." : `${cylinderTypes.length} types available`}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaBuilding className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Brand
                    </Form.Label>
                    <Form.Select
                      name="brandId"
                      value={formData.brandId}
                      onChange={handleChange}
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                      disabled={loadingDropdowns}
                    >
                      <option value="">Select Brand</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                      {loadingDropdowns ? "Loading..." : `${brands.length} brands available`}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaCalendarAlt className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Connection Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="connectionDate"
                      value={formData.connectionDate}
                      onChange={handleChange}
                      required
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaMoneyBillWave className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Security Deposit (₹)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="securityDeposit"
                      value={formData.securityDeposit}
                      onChange={handleChange}
                      step="100"
                      min="0"
                      placeholder="0"
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaTools className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Cylinder Capacity
                    </Form.Label>
                    <Form.Select
                      name="cylinderCapacity"
                      value={formData.cylinderCapacity}
                      onChange={handleChange}
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                    >
                      <option value="5kg">5 kg</option>
                      <option value="14.2kg">14.2 kg</option>
                      <option value="19kg">19 kg</option>
                      <option value="47.5kg">47.5 kg</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Equipment Details Section */}
            <div className="mb-4">
              <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)", fontSize: "15px" }}>
                <FaShieldAlt className="me-2" /> Equipment Details
              </h6>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaTools className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Regulator Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="regulatorNumber"
                      value={formData.regulatorNumber}
                      onChange={handleChange}
                      placeholder="Enter regulator number"
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaTools className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Regulator Type
                    </Form.Label>
                    <Form.Select
                      name="regulatorType"
                      value={formData.regulatorType}
                      onChange={handleChange}
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                    >
                      <option value="standard">Standard</option>
                      <option value="high-pressure">High Pressure</option>
                      <option value="low-pressure">Low Pressure</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                      <FaTools className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                      Stove Details
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="stoveDetails"
                      value={formData.stoveDetails}
                      onChange={handleChange}
                      placeholder="e.g., 2 burner, 4 burner"
                      className="rounded-2"
                      style={{ fontSize: "14px", height: "42px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Status Management (Only for Edit) */}
            {isEditing && (
              <div className="mb-4">
                <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)", fontSize: "15px" }}>
                  <FaPlug className="me-2" /> Status Management
                </h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                        <FaInfoCircle className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                        Connection Status
                      </Form.Label>
                      <Form.Select
                        name="connectionStatus"
                        value={formData.connectionStatus}
                        onChange={handleChange}
                        className="rounded-2"
                        style={{ fontSize: "14px", height: "42px" }}
                      >
                        <option value="active">✅ Active</option>
                        <option value="pending">⏳ Pending</option>
                        <option value="suspended">⏸️ Suspended</option>
                        <option value="terminated">❌ Terminated</option>
                      </Form.Select>
                      <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                        Changing status will update the connection's availability
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex flex-wrap gap-3 mt-4 pt-3 border-top">
              <Button
                type="submit"
                variant="primary"
                disabled={submitting}
                style={{
                  backgroundColor: "rgb(30, 58, 111)",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 28px",
                  fontWeight: "600",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {submitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FaSave /> {isEditing ? "Update Connection" : "Create Connection"}
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate("/connections")}
                style={{ 
                  borderRadius: "8px", 
                  padding: "10px 28px",
                  borderColor: "#dee2e6",
                  color: "#495057",
                  fontWeight: "500",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaTimes /> Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ConnectionForm;